import React, { useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Eye, EyeOff, User, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function HireHubAuthDrawer({ open, onOpenChange, journey, onAuthenticated }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", keep: true });
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim()) {
      toast.error("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
      } else {
        if (!form.name.trim()) {
          toast.error("Enter your name to sign up.");
          setLoading(false);
          return;
        }
        await register(form.name, form.email, form.password);
      }
      toast.success(tab === "login" ? "Welcome back!" : "Account created!");
      onOpenChange(false);
      onAuthenticated?.(journey);
    } catch (err) {
      toast.error(err?.message || "Authentication failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const social = (provider) => {
    toast.info(`${provider} sign-in is coming soon.`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="hire-hub-auth-drawer"
      >
        <SheetHeader className="pb-3">
          <SheetTitle className="font-display text-lg">
            Continue with Hire Hub
          </SheetTitle>
          <SheetDescription className="text-xs">
            Log in or create an account to continue.
          </SheetDescription>
        </SheetHeader>

        <div className="flex gap-1 border-b border-gray-100">
          <TabBtn active={tab === "login"} onClick={() => setTab("login")} label="Log In" testid="auth-tab-login" />
          <TabBtn active={tab === "signup"} onClick={() => setTab("signup")} label="Sign Up" testid="auth-tab-signup" />
        </div>

        <form onSubmit={submit} className="pt-5 space-y-4">
          {tab === "signup" && (
            <Field label="Full Name" placeholder="Enter your name" value={form.name} onChange={(v) => set("name", v)} testid="auth-name" />
          )}
          <Field label="Email / Mobile Number" placeholder="Enter email or mobile number" value={form.email} onChange={(v) => set("email", v)} testid="auth-email" />
          <div>
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Password
            </label>
            <div className="mt-1 flex items-center rounded-xl border border-gray-200 focus-within:border-[#4B2AAD] focus-within:ring-2 focus-within:ring-[#4B2AAD]/15">
              <input
                data-testid="auth-password"
                type={visible ? "text" : "password"}
                value={form.password}
                placeholder="Enter your password"
                onChange={(e) => set("password", e.target.value)}
                className="flex-1 px-3 py-2 outline-none text-sm bg-transparent"
              />
              <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                className="px-3 text-gray-500"
                aria-label={visible ? "Hide password" : "Show password"}
              >
                {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {tab === "login" && (
            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 text-gray-600">
                <input
                  type="checkbox"
                  checked={form.keep}
                  onChange={(e) => set("keep", e.target.checked)}
                  className="accent-[#4B2AAD] h-4 w-4"
                />
                Keep me signed in
              </label>
              <button type="button" className="text-[#4B2AAD] font-semibold hover:underline">
                Forgot Password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit"
            className="dyntra-btn-primary w-full py-3"
          >
            {loading ? "Please wait…" : "Continue"}
          </button>

          <div className="relative py-1">
            <div className="border-t border-gray-100" />
            <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-white px-3 text-xs text-gray-400">
              or
            </span>
          </div>

          <button
            type="button"
            onClick={() => social("Google")}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <span className="h-4 w-4 rounded-full bg-white inline-flex items-center justify-center">G</span>
            Continue with Google
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => social("Apple")}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              🍎 Continue with Apple
            </button>
            <button
              type="button"
              onClick={() => social("Email Link")}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              ✉️ Continue with Email Link
            </button>
          </div>

          <div className="rounded-2xl bg-[#F5F3FB] border border-[#4B2AAD]/15 p-3 text-xs text-gray-700">
            <p className="font-semibold text-gray-900">
              After successful authentication, you&apos;ll return to your selected journey
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <JourneyRow icon={User} label="Hire Me" sub="Professional profile creation" highlight={journey === "hire-me"} />
              <JourneyRow icon={Building2} label="Hire For Me" sub="Hiring requirement submission" highlight={journey === "hire-for-me"} />
            </div>
          </div>

          <p className="text-[10px] text-gray-400 text-center">
            By continuing, you agree to Dyntra&apos;s{" "}
            <a href="/policy/terms" className="text-[#4B2AAD] font-semibold">
              Terms of Use
            </a>{" "}
            and{" "}
            <a href="/policy/privacy" className="text-[#4B2AAD] font-semibold">
              Privacy Policy
            </a>
            .
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function TabBtn({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${
        active
          ? "border-[#4B2AAD] text-[#4B2AAD]"
          : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
    >
      {label}
    </button>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <input
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
      />
    </div>
  );
}

function JourneyRow({ icon: Icon, label, sub, highlight }) {
  return (
    <div
      className={`flex items-start gap-2 rounded-xl p-2 ${
        highlight ? "bg-white border border-[#4B2AAD]/30" : "bg-white/60"
      }`}
    >
      <div className="h-8 w-8 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#4B2AAD]" />
      </div>
      <div>
        <p className="text-xs font-bold text-gray-900">{label}</p>
        <p className="text-[10px] text-gray-500 leading-tight">{sub}</p>
      </div>
    </div>
  );
}
